import { z } from 'zod/v4';

export const CompanyType = {
  EDUCATIONAL_INSTITUTION: 'EDUCATIONAL_INSTITUTION',
  GOVERNMENT_AGENCY: 'GOVERNMENT_AGENCY',
  NONPROFIT: 'NONPROFIT',
  PARTNERSHIP: 'PARTNERSHIP',
  PRIVATELY_HELD: 'PRIVATELY_HELD',
  FAMILY_BUSINESS: 'FAMILY_BUSINESS',
  PUBLIC_COMPANY: 'PUBLIC_COMPANY',
  SELF_EMPLOYED: 'SELF_EMPLOYED',
  SOLE_PROPRIETORSHIP: 'SOLE_PROPRIETORSHIP',
} as const;

export type CompanyType = (typeof CompanyType)[keyof typeof CompanyType];

export const CompanySize = {
  FROM_0_TO_1: 'FROM_0_TO_1',
  FROM_2_TO_10: 'FROM_2_TO_10',
  FROM_11_TO_50: 'FROM_11_TO_50',
  FROM_51_TO_200: 'FROM_51_TO_200',
  FROM_201_TO_500: 'FROM_201_TO_500',
  FROM_501_TO_1000: 'FROM_501_TO_1000',
  FROM_1001_TO_5000: 'FROM_1001_TO_5000',
  FROM_5001_TO_10000: 'FROM_5001_TO_10000',
  FROM_10001_TO_INFINITE: 'FROM_10001_TO_INFINITE',
} as const;

export type CompanySize = (typeof CompanySize)[keyof typeof CompanySize];

export const companyImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  fileType: z.string().optional(),
  originalUrl: z.string().optional(),
});

export const companyGeojsonSchema = z.object({
  type: z.string(),
  geometry: z.object({
    type: z.string(),
    coordinates: z.array(z.number()),
  }),
});

export const companySchema = z.object({
  id: z.string(),
  isDemoCompany: z.number().nullable().optional(),
  isRemoved: z.number().nullable().optional(),
  isClientCompany: z.number().nullable().optional(),
  parentId: z.string().nullable().optional(),

  // About Profile
  officialName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  transliteratedDisplayName: z.string().nullable().optional(),
  transliteratedName: z.string().nullable().optional(),
  transliteratedOfficialName: z.string().nullable().optional(),
  officialNameLocalized: z.string().nullable().optional(),
  internalCompanyCode: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  facilityProcesses: z.string().nullable().optional(),
  imageList: z.preprocess(
    val => (typeof val === 'string' ? JSON.parse(val) : val),
    z.array(companyImageSchema).nullable().optional(),
  ),
  featuredImageFileId: z.string().nullable().optional(),
  featuredImageFileUrl: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  countryName: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  county: z.string().nullable().optional(),
  zipCode: z.string().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  geojson: z.preprocess(
    val => (typeof val === 'string' ? JSON.parse(val) : val),
    companyGeojsonSchema.nullable().optional(),
  ),
  companySize: z.enum(CompanySize).nullable().optional(),
  companyType: z.enum(CompanyType).nullable().optional(),
  videoFileId: z.string().nullable().optional(),
  videoFileUrl: z.string().nullable().optional(),
  numOfPermanentEmployees: z.coerce.number().nullable().optional(),
  numOfMaleEmployees: z.coerce.number().nullable().optional(),
  numOfFemaleEmployees: z.coerce.number().nullable().optional(),

  // Other IDs Profile
  commercialRegistrationNumber: z.string().nullable().optional(),
  commercialRegisterNumber: z.string().nullable().optional(),
  dunsNumber: z.string().nullable().optional(),
  globalLocationNumber: z.string().nullable().optional(),
  higgId: z.string().nullable().optional(),
  manufacturerIdentificationCode: z.string().nullable().optional(),
  osHubId: z.string().nullable().optional(),
  slcpFacilityId: z.string().nullable().optional(),
  textileExchangeId: z.string().nullable().optional(),
  vatId: z.string().nullable().optional(),
  workosOrganizationId: z.string().nullable().optional(),
  zdhcId: z.string().nullable().optional(),

  // Operational Metrics Profile
  annualProductionCapacity: z.string().nullable().optional(),
  annualProductionCapacityUnit: z.string().nullable().optional(),
  minimumOrderQuantity: z.string().nullable().optional(),
  minimumOrderQuantityPerStyle: z.string().nullable().optional(),
  standardLeadTime: z.string().nullable().optional(),

  // Metadata
  createdAt: z.coerce.date().nullable().optional(),
  createdByApiKeyId: z.string().nullable().optional(),
  createdByApiKeyLabel: z.string().nullable().optional(),
  createdByCompanyId: z.string().nullable().optional(),
  createdByImpersonateId: z.string().nullable().optional(),
  createdByUserId: z.string().nullable().optional(),
  createdByUserName: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedByApiKeyId: z.string().nullable().optional(),
  updatedByApiKeyLabel: z.string().nullable().optional(),
  updatedByCompanyId: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  updatedByImpersonateId: z.string().nullable().optional(),
  updatedByImpersonateName: z.string().nullable().optional(),
  updatedByUserId: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),

  // To rething if we want to keep those
  videoFile: z.string().nullable().optional(),
  collaborationStatus: z.string().nullable().optional(),
  collaborationStartDate: z.coerce.date().nullable().optional(),
  retracedRiskScore: z.string().nullable().optional(),
  collaborationEndDate: z.coerce.date().nullable().optional(),
  relationshipType: z.string().nullable().optional(),
  relationshipTypeNames: z.string().nullable().optional(),
  supplyChainTier: z.string().nullable().optional(),
  responsiblePerson: z.string().nullable().optional(),
  responsiblePersonNames: z.string().nullable().optional(),
  vendors: z.string().nullable().optional(),
  vendorsNames: z.string().nullable().optional(),
  agents: z.string().nullable().optional(),
  agentsNames: z.string().nullable().optional(),
});

export type CompanyImage = z.infer<typeof companyImageSchema>;
export type ICompany = z.infer<typeof companySchema>;
